// Figma: 8196:82089 — see stories/blocks/product-comparison-cards-grid.stories.tsx
"use client";

import { Check } from "iconoir-react";

import { ProductTag } from "@/components/products/product-tag";
import { Link } from "@/components/ui/link";
import { cn } from "@/lib/utils";

import type { ProductCardData } from "./product-comparison-cards";

interface ProductComparisonCardsGridProps {
  cards: ProductCardData[];
}

/** Figma 8196:82089 — at xl: white cards 434px, featured 480px, row `items-center`. Below xl, stack so fixed heights do not clip wrapped copy. */
export function ProductComparisonCardsGrid({
  cards,
}: ProductComparisonCardsGridProps) {
  const standardCards = cards.filter((card) => card.variant !== "featured");
  const featuredCard = cards.find((card) => card.variant === "featured");

  return (
    <div className="col-span-full flex flex-col gap-5 xl:flex-row xl:items-center xl:gap-6">
      <div className="grid min-w-0 flex-[2] grid-cols-1 gap-5 md:grid-cols-2 xl:gap-6">
        {standardCards.map((card) => (
          <ProductCard key={card.title} {...card} />
        ))}
      </div>
      {featuredCard ? (
        <div className="min-w-0 flex-1">
          <ProductCard {...featuredCard} />
        </div>
      ) : null}
    </div>
  );
}

export function ProductCard({
  title,
  description,
  tags,
  terms,
  detail,
  cta,
  variant = "default",
}: ProductCardData) {
  const isFeatured = variant === "featured";

  return (
    <article
      className={cn(
        "relative flex flex-col rounded-2xl border",
        isFeatured
          ? "border-grey-100 bg-pulse-700 flex w-full min-w-0 flex-col overflow-hidden p-6 shadow-[0_5px_11px_rgba(0,0,0,0.1),0_21px_21px_rgba(0,0,0,0.09),0_46px_28px_rgba(0,0,0,0.05)] lg:p-16 xl:min-h-[480px]"
          : "border-grey-100 bg-grey-0 min-w-0 p-6 lg:p-16 xl:min-h-[434px]"
      )}
    >
      {isFeatured && <FeaturedGridPattern />}

      <div
        className={cn(
          "relative z-10 flex h-full min-h-0 flex-1 flex-col",
          isFeatured ? "gap-6" : "gap-6 lg:gap-8"
        )}
      >
        <div className="flex flex-col gap-3">
          <div className="flex flex-col gap-2 xl:flex-row xl:flex-wrap">
            {tags.map((tag) => (
              <ProductTag
                key={tag.label}
                label={tag.label}
                variant={tag.variant ?? (isFeatured ? "onColor" : "default")}
              />
            ))}
          </div>
          <h3
            className={cn(
              "text-ds-heading-h4 lg:text-ds-heading-h3 min-w-0 break-words",
              isFeatured ? "text-grey-0" : "text-grey-950"
            )}
          >
            {title}
          </h3>
          <p
            className={cn(
              "text-ds-body-xs lg:text-ds-body-sm min-w-0 break-words",
              isFeatured ? "text-grey-0" : "text-grey-600"
            )}
          >
            {description}
          </p>
        </div>

        {terms.length > 0 && (
          <ul className="flex flex-col gap-2">
            {terms.map((term) => (
              <li key={term} className="flex items-center gap-2.5">
                <span className="bg-grey-300 flex size-4 shrink-0 items-center justify-center rounded-sm">
                  <Check className="text-grey-0 size-3" strokeWidth={2} />
                </span>
                <span className="text-grey-600 text-ds-body-xs lg:text-ds-body-sm min-w-0 break-words">
                  {term}
                </span>
              </li>
            ))}
          </ul>
        )}

        {isFeatured && detail && (
          <div className="flex min-h-0 flex-1 flex-col gap-6">
            <div className="flex flex-col gap-[30px] max-md:hidden">
              <hr className="border-grey-0/20 border-t" />
              <p className="text-grey-0 text-ds-body-sm">{detail}</p>
            </div>
            <p className="text-grey-0 text-ds-body-sm md:hidden">{detail}</p>
            {cta && (
              <Link
                href={cta.href}
                external={cta.external}
                className="border-grey-100 bg-grey-0 text-grey-950 text-ds-label-md mt-auto inline-flex h-8 w-fit shrink-0 items-center justify-center rounded-full border px-4 font-medium transition-opacity hover:opacity-90"
              >
                {cta.label}
              </Link>
            )}
          </div>
        )}

        {!isFeatured && cta && (
          <Link
            href={cta.href}
            external={cta.external}
            className="border-grey-100 bg-grey-0 text-grey-950 text-ds-label-md mt-auto inline-flex h-8 w-fit items-center justify-center rounded-full border px-4 font-medium transition-opacity hover:opacity-90"
          >
            {cta.label}
          </Link>
        )}
      </div>
    </article>
  );
}

function FeaturedGridPattern() {
  return (
    <>
      {/* eslint-disable-next-line @next/next/no-img-element */}
      <img
        src="/images/products/card-grid.png"
        alt=""
        aria-hidden
        className="pointer-events-none absolute top-[-60px] left-[-86px] h-[722px] max-w-none opacity-40 max-xl:top-[-66px] max-xl:left-[-149px] max-xl:h-[454px] max-xl:w-[1270px]"
        draggable={false}
      />
    </>
  );
}
